/**
 * Provenance Helpers
 * 
 * Utilities for working with FieldProvenance in a backward-compatible way.
 * These helpers allow the code to work with both old string values and new
 * provenance-wrapped values seamlessly.
 */

import type { FieldProvenance } from '@core/types';

/**
 * Extract the value from a field that may or may not have provenance
 */
export function getValue<T>(field: T | FieldProvenance<T> | null | undefined): T | null {
  if (field === null || field === undefined) return null;
  if (typeof field === 'object' && field !== null && 'value' in field && 'certainty' in field) {
    return (field as FieldProvenance<T>).value;
  }
  return field as T;
}

/**
 * Get certainty level from a field
 */
export function getCertainty<T>(
  field: T | FieldProvenance<T> | null | undefined
): 'confirmed' | 'assumed' | 'unknown' {
  if (field === null || field === undefined) return 'unknown';
  if (typeof field === 'object' && field !== null && 'certainty' in field) {
    return (field as FieldProvenance<T>).certainty;
  }
  // Legacy: if it's a non-empty value, assume it was confirmed by user
  const value = field as any;
  if (value && value !== 'unknown' && value !== '') {
    return 'confirmed';
  }
  return 'unknown';
}

/**
 * Get source from a field
 */
export function getSource<T>(
  field: T | FieldProvenance<T> | null | undefined
): 'user_entered' | 'inferred' | 'default_display' | 'imported' {
  if (field === null || field === undefined) return 'default_display';
  if (typeof field === 'object' && field !== null && 'source' in field) {
    return (field as FieldProvenance<T>).source;
  }
  // Legacy: assume non-empty values were user entered
  const value = field as any;
  if (value && value !== 'unknown' && value !== '') {
    return 'user_entered';
  }
  return 'default_display';
}

/**
 * Get evidence from a field
 */
export function getEvidence<T>(field: T | FieldProvenance<T> | null | undefined): string | undefined {
  if (typeof field === 'object' && field !== null && 'evidence' in field) {
    return (field as FieldProvenance<T>).evidence;
  }
  return undefined;
}

/**
 * Check if a field has provenance metadata
 */
export function hasProvenance<T>(field: any): field is FieldProvenance<T> {
  return !!(field && typeof field === 'object' && 'value' in field && 'certainty' in field && 'source' in field);
}

/**
 * Create a provenance wrapper for a value
 */
export function createProvenance<T>(
  value: T | null,
  certainty: 'confirmed' | 'assumed' | 'unknown',
  source: 'user_entered' | 'inferred' | 'default_display' | 'imported',
  evidence?: string
): FieldProvenance<T> {
  return {
    value,
    certainty,
    source,
    evidence,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Update a field's provenance while preserving its value
 */
export function updateProvenance<T>(
  field: T | FieldProvenance<T> | null | undefined,
  updates: Partial<Omit<FieldProvenance<T>, 'value'>>
): FieldProvenance<T> {
  const current = hasProvenance<T>(field) ? field : createProvenance(field as T, 'unknown', 'default_display');
  return {
    ...current,
    ...updates,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Mark a field as user-confirmed
 */
export function confirmValue<T>(value: T): FieldProvenance<T> {
  return createProvenance(value, 'confirmed', 'user_entered');
}

/**
 * Mark a field as inferred with evidence
 */
export function inferValue<T>(value: T, evidence: string): FieldProvenance<T> {
  return createProvenance(value, 'assumed', 'inferred', evidence);
}

/**
 * Create an unknown field
 */
export function unknownField<T>(): FieldProvenance<T> {
  return createProvenance<T>(null, 'unknown', 'default_display');
}

/**
 * Check if a value is "empty" (null, undefined, 'unknown', or empty string)
 */
export function isEmpty(value: any): boolean {
  return value === null || value === undefined || value === 'unknown' || value === '';
}

/**
 * Check if a field should be considered "confirmed" for certainty reporting
 */
export function isConfirmed<T>(field: T | FieldProvenance<T> | null | undefined): boolean {
  const certainty = getCertainty(field);
  const value = getValue(field);
  return certainty === 'confirmed' && !isEmpty(value);
}

/**
 * Check if a field should be considered "assumed" for certainty reporting
 */
export function isAssumed<T>(field: T | FieldProvenance<T> | null | undefined): boolean {
  const certainty = getCertainty(field);
  const value = getValue(field);
  return certainty === 'assumed' && !isEmpty(value);
}

/**
 * Check if a field should be considered "unknown" for certainty reporting
 */
export function isUnknown<T>(field: T | FieldProvenance<T> | null | undefined): boolean {
  const certainty = getCertainty(field);
  const value = getValue(field);
  return certainty === 'unknown' || isEmpty(value);
}
